import {jsx} from 'panel';
import {VNode} from 'snabbdom';

import {CheckboxEl} from 'common/components/checkbox';
import {RadioButtonEl} from 'common/components/radio-button';
import {filterAttributes} from 'common/util';
import {SvgIconEl} from 'common/components/svg-icon';
// eslint-disable-next-line import/no-cycle
import {ItemsMenuEl} from 'common/components/items-menu';
import {
  EventHandler,
  ItemAndEventHandler,
  ItemHandler,
  LabelTheme,
  Section,
  SectionItemContextPanelTransform,
  SubLabelTheme,
  WrappedSection,
  WrappedSectionItem,
} from 'common/components/items-menu/types';
import {DropMenuEl} from 'common/components/drop-menu';
import {Directions, PositioningStrategy, PositionParentType} from 'common/components/drop-menu/types';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';

const Label = ({label, labelMatches, showMatches}) => {
  if (labelMatches && showMatches) {
    return (
      <span sel='.label'>
        {labelMatches.map((part) => (
          <span sel='.match-part'>{part}</span>
        ))}
      </span>
    );
  }

  return <span sel='.label'>{label}</span>;
};

const UserControlWrapper = (
  {
    isFocused,
    isRadioButton,
    isCheckbox,
    isAnchorLink,
    item,
  }: {
    isRadioButton: boolean;
    isCheckbox: boolean;
    isAnchorLink: boolean;
    isFocused: boolean;
    item: WrappedSectionItem;
  },
  children: VNode | VNode[],
) => {
  const sectionItem = item.wrapped;
  if (isCheckbox) {
    return (
      <CheckboxEl
        attrs={filterAttributes({
          elref: `checkbox`,
          checked: sectionItem.isSelected,
          /** if color is not provided, will be standard $blue1 */
          'checked-color':
            sectionItem.isSelectAll || sectionItem.isSelectAllMatching ? `none` : sectionItem.color || null,
          disabled: sectionItem.isDisabled,
          'unchecked-icon': sectionItem.isSelectAll || sectionItem.isSelectAllMatching ? `dash` : ``,
          focused: isFocused,
        })}
      >
        {children}
      </CheckboxEl>
    );
  }

  if (isRadioButton) {
    return (
      <RadioButtonEl
        attrs={filterAttributes({
          selected: sectionItem.isSelected,
        })}
      >
        {children}
      </RadioButtonEl>
    );
  }

  if (isAnchorLink) {
    return (
      <a sel='.list-option-link' attrs={{href: sectionItem.href, target: sectionItem.target}}>
        {children}
      </a>
    );
  }
  return <>{children}</>;
};

const SectionItemIcon = ({
  item,
  alignAllText,
  iconRenderer,
  isMultiple,
}: {
  item: WrappedSectionItem;
  alignAllText: boolean;
  iconRenderer: WrappedSection['iconRenderer'];
  isMultiple: boolean;
}) => {
  const sectionItem = item.wrapped;
  const useCheckmark = sectionItem.labelTheme === LabelTheme.InvertedCheckmark;
  const listIcon = sectionItem.icon || (useCheckmark && `checkmark`);

  if (typeof iconRenderer === `function`) {
    return <div sel='.custom-icon'>{iconRenderer(sectionItem)}</div>;
  }

  return (
    (listIcon || alignAllText) && (
      <div
        sel='.option-icon.item-icon'
        attrs={{icon: listIcon}}
        class={{
          [`align-${sectionItem.iconSide || `left`}`]: true,
          'align-top': Boolean(sectionItem.subLabel) && !isMultiple,
          highlighted: sectionItem.highlightIcon,
        }}
      >
        <SvgIconEl attrs={{icon: listIcon, empty: !listIcon}} />
      </div>
    )
  );
};

const SectionItemLabel = ({
  item,
  isRadioButton,
  showMatches,
  isExperimental,
}: {
  item: WrappedSectionItem;
  isRadioButton: boolean;
  showMatches: boolean;
  isExperimental: boolean;
}) => {
  const sectionItem = item.wrapped;
  return (
    <>
      <div
        sel='.option-label-section'
        class={{
          'radio-button': isRadioButton,
          [`sublabel-theme-${sectionItem.subLabelTheme}`]: Boolean(sectionItem.subLabelTheme),
          'has-sublabel': Boolean(sectionItem.subLabel),
        }}
      >
        <div
          sel='.option-label'
          attrs={{
            title: sectionItem.label,
          }}
          class={{
            [`theme-${sectionItem.labelTheme}`]: Boolean(sectionItem.labelTheme),
            truncate: !sectionItem.wrapLabel,
            [`has-expander-info`]: Boolean(sectionItem.isExpander && sectionItem.tooltip),
          }}
        >
          {sectionItem.subLabel && sectionItem.subLabelTheme === SubLabelTheme.Breadcrumb && (
            <span sel='.option-label-path-prefix'>
              <Label
                label={sectionItem.subLabel}
                labelMatches={item.subLabelMatch?.matchParts}
                showMatches={showMatches}
              />
            </span>
          )}
          <Label label={sectionItem.label} labelMatches={item.labelMatch?.matchParts} showMatches={showMatches} />
          {sectionItem.isExpander && sectionItem.tooltip ? (
            <div sel='.section-icon.item-icon'>
              <TooltipEl
                attrs={{
                  placement: Placement.RightCenter,
                  ...(isExperimental && {'position-strategy': PositioningStrategy.Absolute}),
                  ...(sectionItem.tooltip ?? {}),
                }}
              />
              <SvgIconEl attrs={{icon: `info`}} />
            </div>
          ) : (
            {}
          )}
        </div>
        {sectionItem.subLabel && sectionItem.subLabelTheme !== SubLabelTheme.Breadcrumb && (
          <div
            sel='.option-sub-label'
            attrs={{title: sectionItem.subLabel}}
            class={{
              [`theme-${sectionItem.subLabelTheme}`]: Boolean(sectionItem.subLabelTheme),
              truncate: !sectionItem.wrapSubLabel,
            }}
          >
            <Label
              label={sectionItem.subLabel}
              labelMatches={item.subLabelMatch?.matchParts}
              showMatches={showMatches}
            />
          </div>
        )}
      </div>
      {sectionItem.sideLabel && <span sel='.option-side-label'>{sectionItem.sideLabel}</span>}
    </>
  );
};

const SectionItemAction = ({
  item,
  isFocused,
  isPillFocused,
  actionsRenderer,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  item: WrappedSectionItem;
  isFocused: boolean;
  isPillFocused: boolean;
  actionsRenderer: WrappedSection['actionsRenderer'];
  onClick: ItemAndEventHandler;
  onMouseEnter: ItemHandler;
  onMouseLeave: ItemHandler;
}) => {
  const sectionItem = item.wrapped;
  if (typeof actionsRenderer === `function`) {
    return (
      <div
        sel='.custom-actions'
        on={{
          click: (ev) => {
            ev.stopPropagation();
          },
        }}
      >
        {actionsRenderer(sectionItem)}
      </div>
    );
  }

  if (sectionItem.hasPill) {
    return (
      <div
        sel='.secondary-action.pill'
        class={{
          focused: isFocused && isPillFocused,
          disabled: sectionItem.isPillDisabled,
        }}
        on={{
          click: !sectionItem.isPillDisabled ? (ev) => onClick?.(item, ev) : null,
          mouseenter: () => onMouseEnter?.(item),
          mouseleave: () => onMouseLeave?.(item),
        }}
      >
        <span>{sectionItem.pillLabel}</span>
        <div sel='.pill-caret'>
          <SvgIconEl attrs={{icon: `caret-right`}} />
        </div>
      </div>
    );
  }

  if ((sectionItem.subSections && sectionItem.subSections.length) || sectionItem.hasCaret) {
    return (
      <div
        sel='.secondary-action.caret'
        class={{
          persistent: sectionItem.persistCaret,
        }}
      >
        <SvgIconEl attrs={{icon: sectionItem.caretStyle === `arrow` ? `arrow-right` : `caret-right`}} />
      </div>
    );
  }
  return null;
};

export const SectionItemEl = (
  {
    item,
    isMultiple,
    displayAllSelectOne,
    alignAllText,
    isFocused,
    isPillFocused,
    showMatches,
    isExperimental,
    iconRenderer,
    actionsRenderer,
    onActionClick,
    onActionMouseEnter,
    onActionMouseLeave,
  }: {
    item: WrappedSectionItem;
    isMultiple: boolean;
    displayAllSelectOne: boolean;
    alignAllText: boolean;
    isFocused: boolean;
    isPillFocused: boolean;
    showMatches: boolean;
    isExperimental: boolean;
    iconRenderer: WrappedSection['iconRenderer'];
    actionsRenderer: WrappedSection['actionsRenderer'];
    onActionClick: ItemAndEventHandler;
    onActionMouseEnter: ItemHandler;
    onActionMouseLeave: ItemHandler;
  },
  sectionItemSubMenuChild: VNode,
) => {
  const sectionItem = item.wrapped;
  const isRadioButton = isMultiple && displayAllSelectOne;
  const isCheckbox = isMultiple && !sectionItem.isExpander && !sectionItem.isControlItem && !displayAllSelectOne;
  const isAnchorLink = Boolean(sectionItem.href);

  return (
    <UserControlWrapper
      isRadioButton={isRadioButton}
      isCheckbox={isCheckbox}
      isAnchorLink={isAnchorLink}
      isFocused={isFocused}
      item={item}
    >
      <SectionItemIcon item={item} alignAllText={alignAllText} iconRenderer={iconRenderer} isMultiple={isMultiple} />
      <SectionItemLabel
        item={item}
        isRadioButton={isRadioButton}
        showMatches={showMatches}
        isExperimental={isExperimental}
      />
      <SectionItemAction
        item={item}
        isFocused={isFocused}
        isPillFocused={isPillFocused}
        onClick={onActionClick}
        onMouseEnter={onActionMouseEnter}
        onMouseLeave={onActionMouseLeave}
        actionsRenderer={actionsRenderer}
      />
      {sectionItemSubMenuChild}
    </UserControlWrapper>
  );
};

export const SectionItemSubMenuEl = ({
  alignAllText,
  isMultiple,
  disableFlip,
  contextPanelTransform,
  keydownProxyEl,
  onNavigateBack,
  onClickItem,
  onFocusItem,
  sections,
}: {
  alignAllText: boolean;
  isMultiple: boolean;
  disableFlip: boolean;
  contextPanelTransform: SectionItemContextPanelTransform;
  keydownProxyEl: HTMLElement;
  onNavigateBack: () => void;
  onClickItem: EventHandler;
  onFocusItem: EventHandler;
  sections?: Section[];
}) => {
  return (
    <DropMenuEl
      sel='.option-subsections'
      attrs={{
        open: true,
        offset: 10,
        'position-strategy': PositioningStrategy.Absolute,
        direction: Directions.Right,
        'allowed-flip-directions': disableFlip ? [] : undefined,
      }}
      props={{
        // place the dropdown relative to the parent element as opposed to its offset parent
        positionParent: PositionParentType.ParentElement,
      }}
      on={{
        click: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        },
      }}
    >
      <ItemsMenuEl
        attrs={{
          elref: `nestedList`,
          multiple: isMultiple,
          'align-all-text': alignAllText,
        }}
        props={{
          sections,
          contextPanelTransform,
          keydownProxyEl,
        }}
        on={{
          navigateBack: () => onNavigateBack(),
          clickItem: onClickItem,
          focusItem: onFocusItem,
        }}
      />
    </DropMenuEl>
  );
};
